import React, { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Logo } from "./Logo";
import { JaggedFooter } from "./JaggedFooter";
import { Menu, X } from "lucide-react";

const navItems = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/projects", label: "Projects", testid: "nav-link-projects" },
  { to: "/about", label: "About Me", testid: "nav-link-about-me" },
  { to: "/contact", label: "Contact Me", testid: "nav-link-contact-me" },
];

const NavPill = ({ to, label, testid, onClick }) => (
  <NavLink
    to={to}
    end={to === "/"}
    onClick={onClick}
    data-testid={testid}
    className={({ isActive }) =>
      [
        "nav-link-hover px-5 py-2 rounded-full border-2 border-black font-body font-extrabold text-xs md:text-sm uppercase tracking-wider",
        "cursor-pointer shadow-[3px_3px_0_0_rgba(0,0,0,1)]",
        isActive
          ? "bg-pink-600 text-white"
          : "bg-white text-black",
      ].join(" ")
    }
  >
    {label}
  </NavLink>
);

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="bg-y2k-gradient min-h-screen flex flex-col" data-testid="app-shell">
      {/* Header / Nav — translucent cream bar so the dark logo + nav stay legible against the black top of the gradient */}
      <header className="w-full px-4 md:px-10 py-4 md:py-6" data-testid="site-header">
        <div className="max-w-7xl mx-auto bg-[#FEFBEA]/90 backdrop-blur-md border-2 border-black/80 rounded-full shadow-[6px_6px_0_0_rgba(0,0,0,0.85)] px-4 md:px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/" data-testid="logo-link" className="flex items-center">
            <Logo size="md" variant="secondary" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-2" data-testid="desktop-nav">
            {navItems.map((n) => (
              <NavPill key={n.to} {...n} />
            ))}
          </nav>

          {/* Mobile burger */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 rounded-full border-2 border-black bg-white shadow-[3px_3px_0_0_rgba(0,0,0,1)]"
            aria-label="Open menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile drawer */}
        {open && (
          <div
            className="lg:hidden mt-3 max-w-7xl mx-auto bg-[#FEFBEA] border-4 border-black rounded-2xl p-4 shadow-[6px_6px_0_0_rgba(0,0,0,1)] flex flex-col gap-3"
            data-testid="mobile-nav"
          >
            {navItems.map((n) => (
              <NavPill key={n.to} {...n} onClick={() => setOpen(false)} />
            ))}
          </div>
        )}
      </header>

      {/* Page content */}
      <main className="flex-1 w-full" key={location.pathname} data-testid="page-main">
        <div className="sticker-pop">{children}</div>
      </main>

      <JaggedFooter />
    </div>
  );
}
